/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { useRef, useState, useEffect, useMemo } from 'react';
import ReportPage from '../../../components/ReportPage';
import ReportForm, { IReportFormInstance } from '../ReportForm';
import useEditDPSReport from '../../_hooks/useEditDPSReport';
import { getStorageReport } from '../../../utils';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { CARD_TITLE as COMMENT_CARD_TITLE } from '../Comment';
import { CARD_TITLE as CHANGES_CARD_TITLE } from '../UpdatesOfWeek/utils';
import { CARD_TITLE as MEMO_CARD_TITLE } from '../MemoPendingApproval/utils';
import { CARD_TITLE as CALENDAR_CARD_TITLE } from '../../../components/Calendar/utils';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import type { IDPSReportModuleTitles } from '@gp/types/dpsReport/v3';
import type { IDPSReportFormValue } from '../../types';
import Loading from '@gp/components/Loading';
import '../../../scss/index.scss';

const DEFAULT_FORM_VALUE: IDPSReportFormValue = {
  comment: '',
  project_comment: [],
  changes: [],
  memo: [],
  calendar: {
    start_date: '',
    end_date: '',
    list: [],
  },
};

const EditReportV3: React.FC = () => {
  const {
    isGuide,
    isLoading,
    isCalendarInnerLoading,
    setIsCalendarInnerLoading,
    isSubmitting,
    submitType,
    reportHelper,
    projects,
    filter,
    onCalendarRefreshData,
    onSubmit,
    reportParams,
  } = useEditDPSReport({
    setTitles: (payload) => {
      const {
        comment_title: commentTitle,
        changes_title: changesTitle,
        memo_title: memoTitle,
        calendar_title: calendarTitle,
      } = payload || {};
      setTitles({
        comment_title: commentTitle || COMMENT_CARD_TITLE,
        changes_title: changesTitle || CHANGES_CARD_TITLE,
        memo_title: memoTitle || MEMO_CARD_TITLE,
        calendar_title: calendarTitle || CALENDAR_CARD_TITLE,
      });
    },
    getReport: () => ({ ...reportFormValue }),
    setReport: (payload) => {
      setReportFormValue({ ...reportFormValue, ...payload });
    },
    initialize: (payload) => {
      reportFormInstance.current?.initialize(payload);
    },
  });

  const reportFormInstance = useRef<IReportFormInstance | null>(null);

  const defaultTitles = {
    comment_title: 'Important GR & Check-in Updates',
    changes_title: 'P-team Project Updates',
    memo_title: 'Memo Pending Approval',
    calendar_title: 'Calendar',
  };

  const [titles, setTitles] = useState<IDPSReportModuleTitles>(defaultTitles);

  const handleTitleChange = (key: string, payload: string) => {
    setTitles({ ...titles, [key]: payload });
  };

  const [reportFormValue, setReportFormValue] = useState<IDPSReportFormValue>(DEFAULT_FORM_VALUE);
  const [editAutoSaveData, setEditAutoSaveData] = useState<IDPSReportFormValue>(DEFAULT_FORM_VALUE);
  const [editTitlesData, setEditTitlesData] = useState<IDPSReportModuleTitles>(defaultTitles);
  const [isShowReportPage, setIsShowReportPage] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const initialParamsRef = useRef<any>(null);

  useDeepCompareEffect(() => {
    if (initialParamsRef.current === null && reportFormValue !== DEFAULT_FORM_VALUE) {
      initialParamsRef.current = {
        title: titles,
        report: reportFormValue,
      };
    }
  }, [reportFormValue, titles]);

  useDeepCompareEffect(() => {
    setEditAutoSaveData(reportFormValue);
  }, [reportFormValue]);
  useDeepCompareEffect(() => {
    setEditTitlesData(titles);
  }, [titles]);

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_DPSNewsletter_edit}_v3_${filter?.start_date}_${filter?.end_date}`;
  }, [filter]);

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
  });

  useEffect(() => {
    if (isLoading) return;
    setEditAutoSaveData(reportFormValue);
    if (showRestorePrompt(pageKeyCur) && !isLoading) {
      setAutoSaveVisible(true);
      setIsShowReportPage(false);
    } else {
      setIsShowReportPage(true);
    }
  }, [isLoading]);

  const handleAutoSaveConfirm = () => {
    const saveObj = getSaveParamsValue(pageKeyCur) || {};
    setEditAutoSaveData({
      ...reportFormValue,
      ...saveObj.report,
    });
    setEditTitlesData({
      ...titles,
      ...saveObj.title,
    });

    setAutoSaveVisible(false);
    setIsShowReportPage(true);
  };
  const handleAutoSaveCancel = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    setEditAutoSaveData(reportFormValue);
    setEditTitlesData(titles);
    setAutoSaveVisible(false);
    setIsShowReportPage(true);
  };

  const handleConfirm = useMemoizedFn(async (isPublish: boolean) => {
    const storageReport = getStorageReport() || {};
    deleteSaveParamsByPageKey(pageKeyCur);
    onSubmit(titles, { ...reportFormValue }, isPublish, {
      reportId: storageReport?.report_id || '',
      tips: `${CONSTANT_TEXTS.OPERATION_SUCCESS_TIPS}! Unpublished by default`,
    });
  });

  const onOkHandle = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
  };

  return (
    <>
      {isShowReportPage && !isLoading && (
        <ReportPage
          className="dbg-edit-dps-report-page dbg-edit-dps-report-page--v3"
          title="Edit"
          isEdit={true}
          isGuide={isGuide}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          submitType={submitType}
          reportHelper={reportHelper}
          onConfirm={handleConfirm}
          onOkHandle={onOkHandle}
          form={
            <ReportForm
              ref={reportFormInstance}
              isLoading={isLoading}
              isCalendarInnerLoading={isCalendarInnerLoading}
              setIsCalendarInnerLoading={setIsCalendarInnerLoading}
              reportHelper={reportHelper}
              titles={editTitlesData}
              onTitleChange={handleTitleChange}
              projects={projects}
              filter={filter}
              onCalendarRefreshData={onCalendarRefreshData}
              value={showRestorePrompt(pageKeyCur) ? editAutoSaveData : reportFormValue}
              onChange={setReportFormValue}
              initialParams={initialParamsRef.current}
              pageKeyCur={pageKeyCur}
              reportParams={reportParams}
              isShowReportPage={isShowReportPage}
            />
          }
        />
      )}
      {isLoading && (
        <div style={{ height: 66, color: '#262832' }}>
          <Loading className="dbg-dps-report__state-action" loading />
        </div>
      )}
      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </>
  );
};

export default EditReportV3;
